import { useEffect, useState } from "react";
import { Pin, X } from "lucide-react";
import { getAuthUser } from "@/lib/api/token-store";
import { getMessageTime } from "@/lib/messages/messages-helpers";
import {
  getPinnedMessages,
  subscribePinnedMessages,
  unpinMessage,
} from "@/lib/stores/pinned-messages-store";
import { useWorkspaceOwnerId } from "@/lib/workspaces/workspace-owner-context";

/**
 * The "Pinned" lens body — a list of the messages the user has locally bookmarked, newest pin
 * first. Self-contained: it subscribes to the local pinned-messages store and removes pins itself
 * (pins are client-only, no backend). Navigation is the one thing it can't do alone, so opening a
 * pinned message is delegated to `onSelectPin(pin)`. Each pin carries a snapshot captured at pin
 * time, so rows render without loading the message's thread.
 *
 * @param {object} props
 * @param {"normal"|"internal"} [props.scope] Only pins from this scope are shown (defaults to "normal").
 * @param {(pin: object) => void} [props.onSelectPin] Open the pinned message in its thread.
 */
export function PinnedMessagesList({ scope = "normal", onSelectPin }) {
  const [pins, setPins] = useState(() => getPinnedMessages());
  const ownerUserId = useWorkspaceOwnerId();
  useEffect(() => {
    // Re-sync on mount and on every Activity re-show; the subscription is owner-guarded
    // because the profile-switch re-read emits while the OUTGOING pane is still subscribed —
    // accepting it would freeze the incoming user's pins into this workspace's lens.
    setPins(getPinnedMessages());
    return subscribePinnedMessages((next) => {
      if (ownerUserId && String(getAuthUser()?.id || "").trim() !== ownerUserId) return;
      setPins(next);
    });
  }, [ownerUserId]);

  const visible = pins.filter((p) => p.scope === scope);

  if (visible.length === 0) {
    return (
      <div className="flex flex-col items-center gap-1 px-4 py-12 text-center">
        <Pin className="h-5 w-5 text-muted-foreground/60" aria-hidden />
        <p className="text-sm font-medium text-foreground">No pinned messages yet</p>
        <p className="max-w-[18rem] text-xs text-muted-foreground">
          Hover a message in a conversation and click the pin to save it here for later.
        </p>
      </div>
    );
  }

  return (
    <ul className="flex flex-col py-1">
      {visible.map((pin) => (
        <li key={`${pin.threadId}:${pin.messageId}`}>
          <div className="group/pin relative flex items-start gap-2 px-3 py-2.5 transition hover:bg-muted/50">
            <button
              type="button"
              onClick={() => onSelectPin?.(pin)}
              title="Go to this message"
              className="min-w-0 flex-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:rounded-md"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-xs font-semibold text-foreground">
                  {pin.senderLabel || "Message"}
                </span>
                {pin.createdAt ? (
                  <span className="shrink-0 text-[11px] text-muted-foreground">
                    {getMessageTime({ createdAt: pin.createdAt })}
                  </span>
                ) : null}
              </div>
              <p className="mt-0.5 line-clamp-2 wrap-break-word text-xs text-muted-foreground">
                {pin.preview || "(no preview)"}
              </p>
              {pin.threadTitle ? (
                <p className="mt-0.5 truncate text-[11px] text-muted-foreground/80">{pin.threadTitle}</p>
              ) : null}
            </button>
            <button
              type="button"
              aria-label="Unpin message"
              title="Unpin"
              onClick={() => unpinMessage(pin.threadId, pin.messageId)}
              className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground opacity-0 transition hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover/pin:opacity-100"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}
